import { Box, Tab, Tabs } from '@mui/material'
import { Link } from 'react-router-dom'
import { type LicensedEstablishment } from 'src/types/api'
import { useCurrentCorporateAccountStore } from 'stores/useCurrentCorporateAccountStore'
import { useSetLe } from 'stores/useSetLe'
import { useEffect } from 'react'
import { getLEIdsByPermission } from 'utils/permissions'
import { sortComparatorByStandardName } from 'utils/util'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { ErrorIndicator } from 'components/Shared/ErrorIndicator'
import { SelectLicensedEstablishment } from 'components/Shared/SelectLicensedEstablishment'
import { NoDataIndicator } from 'components/Shared/NoDataIndicator'
import NoPatronsCheckedInIcon from 'assets/NoPatronsCheckedIn.png'
import { useSetMe } from 'stores/useMeQuery'

export const PlayerPaybackReportsTabs = ({
  currentTab,
  locationId,
}: {
  currentTab: string
  locationId: number
}) => {
  const { passedMe } = useSetMe()
  const meQuery = passedMe

  const selectedCorporateAccountId =
    useCurrentCorporateAccountStore().currentCorporateAccountId

  const licensedEstablishments = meQuery.data?.licensedEstablishments?.filter(
    (le) => le.corporateAccountId === selectedCorporateAccountId
  )

  // Get LE Ids for the permission the user has access to
  const leIdsByPermission: number[] = getLEIdsByPermission(
    meQuery.data,
    selectedCorporateAccountId,
    'playerPaybackReportsPermission'
  )

  // Filter data by permission
  const licensedEstablishmentsFiltered = licensedEstablishments?.filter((x) =>
    leIdsByPermission.includes(x.id)
  )

  const sortedLicensedEstablishments = licensedEstablishmentsFiltered
    ? [...licensedEstablishmentsFiltered].sort(sortComparatorByStandardName)
    : []

  const setPassedLe = useSetLe((state) => state.setPassedLe)
  const { passedLe } = useSetLe()

  // when the user switches Corp accounts using the select at the left of the screen, set the selected LE to either the one saved in the store or the first available LE if one hasn't been saved
  useEffect(() => {
    setPassedLe(passedLe ?? sortedLicensedEstablishments?.[0])
  }, [selectedCorporateAccountId])

  if (meQuery.isPending) {
    return <ActivityIndicator />
  }

  if (meQuery.isError) {
    return <ErrorIndicator />
  }

  const nonFinancialTabs = [
    {
      label: 'Patron Activity',
      value: `1`,
      to: `/PlayerPaybackReports/PatronActivity`,
    },
    {
      label: 'Patron Stats',
      value: `4`,
      to: `/PlayerPaybackReports/PatronStats`,
    },
  ]

  const financialTabs = [
    {
      label: 'Patron List',
      value: `2`,
      to: `/PlayerPaybackReports/PatronList`,
    },
    {
      label: 'Patron Reward Redemptions',
      value: `3`,
      to: `/PlayerPaybackReports/PatronRewardRedemptions`,
    },
  ]

  if (leIdsByPermission.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          rowGap: '16px',
          borderWidth: 1,
          borderColor: 'grey.300',
          borderRadius: 2,
          px: '16px',
          py: '16px',
          mt: '1px',
        }}
      >
        <NoDataIndicator
          noImage
          noDataMessage="Your current permissions do not allow access to view reports. To obtain access, please contact your Account Manager for assistance."
        />
      </Box>
    )
  }

  if (
    !sortedLicensedEstablishments ||
    sortedLicensedEstablishments.length === 0
  ) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          rowGap: '16px',
          borderWidth: 1,
          borderColor: 'grey.300',
          borderRadius: 2,
          px: '16px',
          py: '16px',
          mt: '1px',
        }}
      >
        <NoDataIndicator
          imageSource={NoPatronsCheckedInIcon}
          noDataMessage="Cannot view reports because there are no locations available for this corporate account"
        />
      </Box>
    )
  }

  return (
    <div>
      <SelectLicensedEstablishment
        onSelectLicensedEstablishment={(selectedLe: LicensedEstablishment) => {
          // Update the passed LE
          setPassedLe(selectedLe)
        }}
        locationId={passedLe?.id}
        permission={'playerPaybackReportsPermission'}
      />
      <Tabs
        value={currentTab}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        {leIdsByPermission.length > 0 &&
          nonFinancialTabs.map((tab) => (
            <Tab
              key={tab.label}
              label={tab.label}
              value={tab.value}
              to={tab.to}
              component={Link}
              disabled={passedLe === undefined}
            />
          ))}
        {leIdsByPermission.length > 0 &&
          financialTabs.map((tab) => (
            <Tab
              key={tab.label}
              label={tab.label}
              value={tab.value}
              to={tab.to}
              component={Link}
              disabled={isNaN(locationId) && passedLe === undefined}
            />
          ))}
      </Tabs>
    </div>
  )
}
